import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getProviders, getProvider, createProvider, updateProvider,
  verifyProvider, disableProvider, deleteProvider, resetProviderPassword
} from '../../services/adminApi';
import { toast } from 'react-toastify';

const ORG_TYPES = ['hospital', 'clinic', 'pharmacy', 'emergency', 'insurance', 'other'];

// ── Create Provider Modal ──────────────────────────────────────
function CreateProviderModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    organizationName: '', organizationType: 'clinic', email: '',
    password: '', confirmPassword: '', licenseNumber: '',
    contactFirstName: '', contactLastName: '', contactPhone: '',
    city: '', state: '', zipCode: '', country: 'US',
    isVerified: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async e => {
    e.preventDefault(); setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await createProvider({
        email: form.email, password: form.password,
        organizationName: form.organizationName,
        organizationType: form.organizationType,
        licenseNumber: form.licenseNumber,
        contactPerson: { firstName: form.contactFirstName, lastName: form.contactLastName, phone: form.contactPhone },
        address: { city: form.city, state: form.state, zipCode: form.zipCode, country: form.country },
        isVerified: form.isVerified
      });
      toast.success(res.data.message);
      onCreated(); onClose();
    } catch (err) { setError(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const field = (label, key, type = 'text', extra = {}) => (
    <div>
      <label className="form-label">{label}</label>
      <input className="admin-input" type={type} value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} {...extra} />
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header"><h3>🏥 Create Provider</h3><button className="btn-icon" onClick={onClose}>✕</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {error && <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', color: 'var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13 }}>⚠ {error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              {field('Organisation Name *', 'organizationName', 'text', { required: true })}
              <div>
                <label className="form-label">Type *</label>
                <select className="admin-select" style={{ width: '100%' }} value={form.organizationType}
                  onChange={e => setForm(f => ({ ...f, organizationType: e.target.value }))}>
                  {ORG_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
            </div>

            {field('Email *', 'email', 'email', { required: true })}
            {field('License Number', 'licenseNumber')}

            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginTop: 4 }}>Contact Person</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {field('First Name', 'contactFirstName')}
              {field('Last Name', 'contactLastName')}
              {field('Phone', 'contactPhone', 'tel')}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginTop: 4 }}>Address</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
              {field('City', 'city')}
              {field('State', 'state')}
              {field('ZIP', 'zipCode')}
              {field('Country', 'country')}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
              <span style={{ fontSize: 13 }}>Mark as verified</span>
              <button type="button" onClick={() => setForm(f => ({ ...f, isVerified: !f.isVerified }))}
                style={{ width: 40, height: 22, background: form.isVerified ? 'var(--accent)' : 'var(--surface3)', border: 'none', borderRadius: 11, cursor: 'pointer', position: 'relative', padding: 0 }}>
                <span style={{ position: 'absolute', top: 3, left: form.isVerified ? 20 : 3, width: 16, height: 16, background: '#fff', borderRadius: '50%', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {field('Password *', 'password', 'password', { required: true, minLength: 8, autoComplete: 'new-password' })}
              {field('Confirm Password *', 'confirmPassword', 'password', { required: true, autoComplete: 'new-password' })}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Create Provider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Provider Detail / Edit Modal ───────────────────────────────
function ProviderModal({ providerId, onClose, onRefresh }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]       = useState('info');
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({});
  const [disableReason, setDisableReason] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [newPw, setNewPw]   = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  useEffect(() => {
    if (!providerId) return;
    getProvider(providerId)
      .then(r => {
        setData(r.data);
        const p = r.data.provider;
        setForm({
          organizationName: p.organizationName || '', organizationType: p.organizationType || 'clinic',
          licenseNumber: p.licenseNumber || '', email: p.email || '',
          contactFirstName: p.contactPerson?.firstName || '', contactLastName: p.contactPerson?.lastName || '',
          contactPhone: p.contactPerson?.phone || '',
          city: p.address?.city || '', state: p.address?.state || '',
          zipCode: p.address?.zipCode || '', country: p.address?.country || 'US'
        });
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load'));
  }, [providerId]);

  const p = data?.provider;

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      await updateProvider(providerId, {
        organizationName: form.organizationName, organizationType: form.organizationType,
        licenseNumber: form.licenseNumber, email: form.email,
        contactPerson: { firstName: form.contactFirstName, lastName: form.contactLastName, phone: form.contactPhone },
        address: { city: form.city, state: form.state, zipCode: form.zipCode, country: form.country }
      });
      toast.success('Provider updated'); onRefresh();
    } catch (err) { setError(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleVerify = async val => {
    setLoading(true);
    try { await verifyProvider(providerId, val); toast.success(val ? 'Verified' : 'Unverified'); onRefresh();
      setData(d => ({ ...d, provider: { ...d.provider, isVerified: val } }));
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  };

  const handleDisable = async val => {
    setLoading(true);
    try { await disableProvider(providerId, val, disableReason); toast.success(val ? 'Disabled' : 'Enabled'); onRefresh();
      setData(d => ({ ...d, provider: { ...d.provider, disabled: val } })); setShowDisableForm(false);
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  };

  const handleResetPw = async () => {
    setError('');
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    if (newPw.length < 8) { setError('Min 8 characters'); return; }
    setLoading(true);
    try { await resetProviderPassword(providerId, newPw); toast.success('Password updated'); setNewPw(''); setConfirmPw(''); }
    catch (err) { setError(err.response?.data?.message || 'Failed'); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Permanently delete this provider?')) return;
    try { await deleteProvider(providerId, true); toast.success('Deleted'); onRefresh(); onClose(); }
    catch { toast.error('Failed'); }
  };

  const TABS = ['info', 'agents', 'password', 'actions'];

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>🏥 {p?.organizationName || 'Provider'}</h3>
          {p && <div style={{ display: 'flex', gap: 6 }}>
            <span className={`badge ${p.isVerified ? 'badge-green' : 'badge-grey'}`}>{p.isVerified ? '✓ Verified' : 'Unverified'}</span>
            {p.disabled && <span className="badge badge-red">Disabled</span>}
          </div>}
          <button className="btn-icon" style={{ marginLeft: 6 }} onClick={onClose}>✕</button>
        </div>

        {!p ? <div className="modal-body"><div className="empty-state"><div className="spinner" /></div></div> : (
          <>
            <div style={{ display: 'flex', gap: 0, padding: '0 20px', borderBottom: '1px solid var(--border)' }}>
              {TABS.map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '10px 12px',
                  fontSize: 12, fontWeight: 600, textTransform: 'capitalize', fontFamily: 'inherit',
                  color: tab === t ? 'var(--accent)' : 'var(--text3)',
                  borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent', marginBottom: -1
                }}>{t}</button>
              ))}
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {error && <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', color: 'var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13 }}>⚠ {error}</div>}

              {tab === 'info' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                    <div><label className="form-label">Organisation Name</label><input className="admin-input" value={form.organizationName} onChange={e => setForm(f => ({ ...f, organizationName: e.target.value }))} /></div>
                    <div><label className="form-label">Type</label>
                      <select className="admin-select" style={{ width: '100%' }} value={form.organizationType} onChange={e => setForm(f => ({ ...f, organizationType: e.target.value }))}>
                        {ORG_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div><label className="form-label">Email</label><input className="admin-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
                    <div><label className="form-label">License #</label><input className="admin-input" value={form.licenseNumber} onChange={e => setForm(f => ({ ...f, licenseNumber: e.target.value }))} /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div><label className="form-label">Contact First</label><input className="admin-input" value={form.contactFirstName} onChange={e => setForm(f => ({ ...f, contactFirstName: e.target.value }))} /></div>
                    <div><label className="form-label">Contact Last</label><input className="admin-input" value={form.contactLastName} onChange={e => setForm(f => ({ ...f, contactLastName: e.target.value }))} /></div>
                    <div><label className="form-label">Phone</label><input className="admin-input" type="tel" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                    <div><label className="form-label">City</label><input className="admin-input" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} /></div>
                    <div><label className="form-label">State</label><input className="admin-input" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} /></div>
                    <div><label className="form-label">ZIP</label><input className="admin-input" value={form.zipCode} onChange={e => setForm(f => ({ ...f, zipCode: e.target.value }))} /></div>
                    <div><label className="form-label">Country</label><input className="admin-input" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} /></div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
                    <button className="btn btn-primary" onClick={handleSave} disabled={loading}>{loading ? <span className="spinner" /> : 'Save Changes'}</button>
                  </div>
                </>
              )}

              {tab === 'agents' && (
                p.agents?.length ? (
                  <table className="admin-table"><thead><tr><th>Name</th><th>Email</th><th>Status</th></tr></thead>
                    <tbody>{p.agents.map(a => (
                      <tr key={a._id}><td style={{ fontWeight: 600 }}>{a.firstName} {a.lastName}</td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{a.email}</td>
                        <td><span className={`badge ${a.isActive ? 'badge-green' : 'badge-red'}`}>{a.isActive ? 'Active' : 'Inactive'}</span></td></tr>
                    ))}</tbody></table>
                ) : <div className="empty-state"><div className="empty-icon">👥</div><p>No agents</p></div>
              )}

              {tab === 'password' && (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>Set a new password for this provider's account.</p>
                  <div><label className="form-label">New Password</label><input className="admin-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" /></div>
                  <div><label className="form-label">Confirm</label><input className="admin-input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} autoComplete="new-password" /></div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={handleResetPw} disabled={loading || !newPw}>{loading ? <span className="spinner" /> : 'Update Password'}</button>
                  </div>
                </>
              )}

              {tab === 'actions' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Verification</div>
                    <button className={`btn btn-sm ${p.isVerified ? 'btn-secondary' : 'btn-success'}`} onClick={() => handleVerify(!p.isVerified)} disabled={loading}>
                      {p.isVerified ? 'Remove Verification' : '✓ Verify Provider'}
                    </button>
                  </div>
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Account Access</div>
                    {p.disabled ? (
                      <button className="btn btn-sm btn-success" onClick={() => handleDisable(false)} disabled={loading}>↺ Re-enable</button>
                    ) : !showDisableForm ? (
                      <button className="btn btn-sm btn-danger" onClick={() => setShowDisableForm(true)}>⊘ Disable</button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input className="admin-input" placeholder="Reason (optional)" value={disableReason} onChange={e => setDisableReason(e.target.value)} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDisable(true)} disabled={loading}>Confirm</button>
                          <button className="btn btn-sm btn-secondary" onClick={() => setShowDisableForm(false)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', borderRadius: 'var(--radius)', padding: 14 }}>
                    <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 6 }}>Delete</div>
                    <button className="btn btn-sm btn-danger" onClick={handleDelete}>🗑 Delete Provider</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────
export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('');
  const [page, setPage]       = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getProviders({ page, limit: 20, search: search || undefined, isVerified: filter === 'verified' ? true : filter === 'unverified' ? false : undefined })
      .then(r => { setProviders(r.data.providers); setPagination(r.data.pagination); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [page, search, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Providers">
      <div className="toolbar">
        <div className="search-wrap">
          <span className="search-icon">⌕</span>
          <input className="admin-input" placeholder="Search organisation or email…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="admin-select" value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
          <option value="">All</option><option value="verified">Verified</option><option value="unverified">Unverified</option>
        </select>
        <div className="toolbar-right">
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{pagination.total ?? '—'} providers</span>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Create Provider</button>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead><tr><th>Organisation</th><th>Type</th><th>Email</th><th>Verified</th><th>Status</th><th>Joined</th><th></th></tr></thead>
          <tbody>
            {loading ? Array.from({ length: 6 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 7 }).map((_, j) => <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>)}</tr>
            )) : providers.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><div className="empty-icon">🏥</div><p>No providers found</p></div></td></tr>
            ) : providers.map(p => (
              <tr key={p._id}>
                <td style={{ fontWeight: 600 }}>{p.organizationName}</td>
                <td style={{ textTransform: 'capitalize', color: 'var(--text2)', fontSize: 12 }}>{p.organizationType}</td>
                <td style={{ color: 'var(--text2)', fontSize: 12 }}>{p.email}</td>
                <td><span className={`badge ${p.isVerified ? 'badge-green' : 'badge-grey'}`}>{p.isVerified ? '✓ Yes' : 'No'}</span></td>
                <td><span className={`badge ${p.disabled ? 'badge-red' : 'badge-green'}`}>{p.disabled ? 'Disabled' : 'Active'}</span></td>
                <td style={{ color: 'var(--text3)', fontSize: 12 }}>{new Date(p.createdAt).toLocaleDateString()}</td>
                <td><button className="btn btn-sm btn-secondary" onClick={() => setSelectedId(p._id)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {pagination.pages > 1 && (
          <div className="pagination">
            <span className="page-info">Page {pagination.page} of {pagination.pages}</span>
            <div className="page-btns">
              <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <button className="page-btn" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateProviderModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {selectedId && <ProviderModal providerId={selectedId} onClose={() => setSelectedId(null)} onRefresh={load} />}
    </AdminLayout>
  );
}
